import { AnySchema, ObjectSchema } from 'yup';
import React, {
  ChangeEvent,
  createContext,
  FC,
  FormHTMLAttributes,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Awaitable } from '@idk-web/core-utils';
import { Validator } from '@/hooks/validator';

type Form<T extends Record<string, unknown>> = {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid: boolean;
  getFormProps(): FormHTMLAttributes<never>;
  getFieldProps(
    field: keyof T,
    fieldType?: HTMLInputTypeAttribute | 'select',
  ): InputHTMLAttributes<never>;
  setTouched(field: keyof T, value: boolean): void;
  setError(field: keyof T, error: string | undefined): void;
  setErrors(errors: Partial<Record<keyof T, string>>): void;
  setValue<U extends keyof T>(
    field: U,
    value: T[U],
    shouldValidate?: boolean,
  ): void;
  setValues(values: Partial<T>, shouldValidate?: boolean): void;
  validate(): Promise<boolean>;
  submit(): void;
  reset(): void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = createContext<Form<any> | null>(null);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormProvider: FC<PropsWithChildren<{ form: Form<any> }>> = ({
  form,
  children,
}) => {
  return <FormContext.Provider value={form}>{children}</FormContext.Provider>;
};

type Options<T extends Record<string, unknown>> = {
  initialValues: T;
  validation?: Partial<{
    [K in keyof T]: (value: T[K]) => Awaitable<string | undefined>;
  }>;
  validate?(values: T): Awaitable<Partial<Record<keyof T, string>>>;
  onSubmit?(values: T): Awaitable<void>;
};

/**
 * A hook for managing form state and validation.
 */
export function useForm<T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validation,
  validate,
}: Options<T>): Form<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateTouched = useCallback((field: keyof T, value: boolean) => {
    setTouched((prev) => {
      if (prev[field] === value) {
        return prev;
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }, []);

  const updateError = useCallback(
    (field: keyof T, error: string | undefined) => {
      setErrors((prev) => {
        if (prev[field] === error) {
          return prev;
        }

        return {
          ...prev,
          [field]: error,
        };
      });
    },
    [],
  );

  const updateErrors = useCallback(
    (errors: Partial<Record<keyof T, string>>) => {
      setErrors((prev) => ({ ...prev, ...errors }));
    },
    [],
  );

  const runValidation = useCallback(
    async (values: T): Promise<boolean> => {
      if (validate) {
        const errors = await validate(values);

        setErrors(errors);

        return Object.values(errors).every((e) => e === undefined);
      }

      if (validation) {
        const entries = await Promise.all(
          Object.entries(values).map(async ([name, value]) => {
            const fn = validation[name as keyof T];

            if (!fn) {
              return [name, undefined] as const;
            }

            return [name, await fn(value as T[keyof T])] as const;
          }),
        );
        const errors = Object.fromEntries(entries) as Partial<
          Record<keyof T, string>
        >;

        setErrors(errors);

        return entries.every(([, e]) => e === undefined);
      }

      return true;
    },
    [validate, validation],
  );

  const updateValue = useCallback(
    <U extends keyof T>(field: U, value: T[U], shouldValidate = true) => {
      if (values[field] === value) {
        return;
      }

      const next = { ...values, [field]: value };

      setValues(next);

      if (shouldValidate) {
        void runValidation(next);
      }
    },
    [values, runValidation],
  );

  const updateValues = useCallback(
    (newValues: Partial<T>, shouldValidate = true) => {
      const next = { ...values, ...newValues };

      setValues(next);

      if (shouldValidate) {
        void runValidation(next);
      }
    },
    [values, runValidation],
  );

  const submit = useCallback(async () => {
    if (!(await runValidation(values))) {
      return;
    }

    if (!onSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, values, runValidation]);

  const getFormProps = useCallback((): FormHTMLAttributes<never> => {
    return {
      onSubmit(e) {
        e.preventDefault();

        void submit();
      },
    };
  }, [submit]);

  const getFieldProps = useCallback(
    (
      field: keyof T,
      fieldType: HTMLInputTypeAttribute | 'select',
    ): InputHTMLAttributes<never> => {
      const props = {
        name: String(field),
        value: values[field],
        type: fieldType,
        onChange(e) {
          const { name, value, type } = e.target;
          const fieldValue =
            type === 'checkbox'
              ? (e.target as HTMLInputElement).checked
              : value;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updateValue(name, fieldValue as any, true);
        },
        onBlur(e) {
          const { name } = e.target;

          updateTouched(name, true);
        },
      } as InputHTMLAttributes<HTMLInputElement | HTMLSelectElement>;

      if (fieldType === 'checkbox') {
        return {
          ...props,
          checked: Boolean(props.value),
        };
      }

      return props;
    },
    [values, updateValue, updateTouched],
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const isValid = Object.values(errors).every((e) => !e);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    submit,
    getFormProps,
    getFieldProps,
    setTouched: updateTouched,
    setError: updateError,
    setErrors: updateErrors,
    setValue: updateValue,
    setValues: updateValues,
    validate: () => runValidation(values),
    reset,
  };
}

type Field<T> = {
  name: string;
  value: T;
  error?: string;
  touched: boolean;
  handleBlur(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
  handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
  getFieldProps(): InputHTMLAttributes<never>;
  setTouched(value: boolean): void;
  setValue(value: T, shouldValidate?: boolean): void;
};

export function useField<T = unknown>(
  name: string,
  fieldType: HTMLInputTypeAttribute | 'select',
): Field<T> {
  const form = useContext(FormContext);

  if (!form) {
    throw new Error('useField must be used within a FormProvider');
  }

  if (!(name in form.values)) {
    throw new Error(`Field "${name}" does not exist in form`);
  }

  return useMemo(
    () => ({
      name,
      value: form.values[name] as T,
      error: form.errors[name],
      touched: form.touched[name] ?? false,
      handleBlur(e) {
        const { name } = e.target;

        form.setTouched(name, true);
      },
      handleChange(e) {
        const { name, value, type } = e.target;
        const fieldValue =
          type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setValue(name, fieldValue as any, true);
      },
      getFieldProps() {
        return form.getFieldProps(name, fieldType);
      },
      setTouched(value) {
        form.setTouched(name, value);
      },
      setValue(value, shouldValidate = true) {
        form.setValue(name, value, shouldValidate);
      },
    }),
    [name, form.values[name], form.errors[name], form.touched[name]],
  );
}

export function yupAdapter<T extends Record<string, unknown>>(
  schema: ObjectSchema<T>,
): Partial<{
  [K in keyof T]: (value: T[K]) => Awaitable<string | undefined>;
}> {
  return Object.entries(schema.fields).reduce(
    (acc, [name, field]: [keyof T, AnySchema]) => ({
      ...acc,
      [name]: async (value: T[typeof name]) => {
        try {
          await field.validate(value, {
            strict: true,
            abortEarly: false,
          });

          return undefined;
        } catch (error) {
          if (error instanceof Error) {
            return error.message;
          }

          return String(error);
        }
      },
    }),
    {},
  );
}

type YupOptions<T extends Record<string, unknown>> = {
  initialValues: T;
  validationSchema?: ObjectSchema<T>;
  onSubmit?(values: T): Awaitable<void>;
};

export function useYupForm<T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validationSchema,
}: YupOptions<T>): Form<T> {
  const validation = useMemo(
    () => (validationSchema ? yupAdapter(validationSchema) : undefined),
    [validationSchema],
  );

  return useForm({
    initialValues,
    onSubmit,
    validation,
  });
}

function flattenValidationError(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }

  if (error === null || error === undefined) {
    return undefined;
  }

  if (typeof error === 'object') {
    for (const value of Object.values(error)) {
      const leaf = flattenValidationError(value);

      if (leaf !== undefined) {
        return leaf;
      }
    }
  }

  return undefined;
}

type ValidatedOptions<T extends Record<string, unknown>> = {
  initialValues: T;
  validator?: Validator<T>;
  onSubmit?(values: T): Awaitable<void>;
};

export function useValidatedForm<T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validator,
}: ValidatedOptions<T>): Form<T> {
  const validate = useMemo(
    () =>
      validator
        ? (values: T): Partial<Record<keyof T, string>> => {
            const result = validator.validate(values);

            if (result.valid) {
              return {};
            }

            if (typeof result.errors === 'string') {
              return {};
            }

            const out: Partial<Record<keyof T, string>> = {};

            for (const key of Object.keys(result.errors)) {
              const e = (result.errors as Record<string, unknown>)[key];
              const flat = flattenValidationError(e);

              if (flat !== undefined) {
                out[key as keyof T] = flat;
              }
            }

            return out;
          }
        : undefined,
    [validator],
  );

  return useForm({
    initialValues,
    onSubmit,
    validate,
  });
}
